// frontend/src/pages/customer/SignPage.jsx
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getPublicAgreement, signAgreement } from '../../lib/api';
import { C, Btn, Modal, Spinner, fmtDate } from '../../components/UI';

export default function SignPage() {
  const { id } = useParams();
  const [agreement, setAgreement] = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');
  const [sigName,   setSigName]   = useState('');
  const [confirm,   setConfirm]   = useState(false);
  const [signing,   setSigning]   = useState(false);
  const [sigErr,    setSigErr]    = useState('');
  const [signed,    setSigned]    = useState(false);

  useEffect(() => {
    getPublicAgreement(id)
      .then(d => { setAgreement(d); if (d.status === 'signed') setSigned(true); })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [id]);

  async function handleSign() {
    setSigning(true); setSigErr('');
    try {
      await signAgreement(id, sigName);
      setAgreement(prev => ({ ...prev, status: 'signed', signed_at: new Date().toISOString(), signature_name: sigName.trim() }));
      setSigned(true); setConfirm(false);
    } catch (err) { setSigErr(err.message); }
    finally { setSigning(false); }
  }

  if (loading) return (
    <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Spinner size={44} />
    </div>
  );

  if (error) return (
    <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ textAlign: 'center', maxWidth: 440 }}>
        <div style={{ fontSize: 60, marginBottom: 20 }}>🔍</div>
        <div style={{ fontSize: 22, fontWeight: 700, color: C.text, marginBottom: 12 }}>Agreement Not Found</div>
        <div style={{ fontSize: 14, color: C.muted, lineHeight: 1.7 }}>{error}<br />Please contact AKCODA for assistance.</div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: C.bg, padding: '40px 16px', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'fixed', width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle,rgba(64,61,212,0.1) 0%,transparent 70%)', top: -100, right: -100, pointerEvents: 'none' }} />

      {confirm && (
        <Modal title="Confirm Your Signature" onClose={() => setConfirm(false)} width={440}>
          <p style={{ fontSize: 14, color: C.muted, marginBottom: 12 }}>You are about to sign this agreement as:</p>
          <div style={{ fontSize: 24, fontFamily: 'Georgia,serif', color: '#a0c4ff', padding: '16px 20px', background: 'rgba(64,61,212,0.15)', border: `1px solid rgba(64,61,212,0.3)`, borderRadius: 10, marginBottom: 20, textAlign: 'center' }}>
            {sigName}
          </div>
          {sigErr && <div style={{ background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.3)', borderRadius: 8, padding: '10px 14px', color: C.danger, fontSize: 13, marginBottom: 16 }}>{sigErr}</div>}
          <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.7, marginBottom: 24 }}>
            By confirming, you <strong style={{ color: C.text }}>legally agree</strong> to all terms in this agreement. This action is binding and cannot be undone.
          </p>
          <div style={{ display: 'flex', gap: 10 }}>
            <Btn variant="primary" disabled={signing} onClick={handleSign} style={{ flex: 1, justifyContent: 'center', boxShadow: `0 4px 16px ${C.brandGlow}` }}>
              {signing ? 'Signing…' : '✓ Confirm & Sign'}
            </Btn>
            <Btn variant="ghost" onClick={() => setConfirm(false)}>Go Back</Btn>
          </div>
        </Modal>
      )}

      <div style={{ maxWidth: 700, margin: '0 auto' }}>
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 20, overflow: 'hidden', boxShadow: '0 24px 80px rgba(0,0,0,0.5)' }}>
          {/* Header */}
          <div style={{ background: 'linear-gradient(135deg,#403DD4 0%,#6a68e8 100%)', padding: '28px 38px', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', right: -30, top: -30, width: 160, height: 160, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
            <div style={{ fontSize: 11, letterSpacing: 3, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', marginBottom: 8 }}>AKCODA</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#fff', marginBottom: 8 }}>{agreement.title}</div>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.65)' }}>
              For <strong style={{ color: 'rgba(255,255,255,0.9)' }}>{agreement.customer_name}</strong> · {fmtDate(agreement.sent_at)}
            </div>
          </div>

          {/* Content */}
          <div style={{ padding: '32px 38px' }}>
            <div style={{ fontSize: 14, color: '#b8b8d0', lineHeight: 1.95, whiteSpace: 'pre-wrap', marginBottom: 36 }}>{agreement.content}</div>

            <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 30 }}>
              {signed ? (
                <div style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: 16, padding: 32, textAlign: 'center' }}>
                  <div style={{ fontSize: 52, marginBottom: 16 }}>✅</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: C.success, marginBottom: 10 }}>Agreement Signed Successfully</div>
                  <div style={{ fontSize: 14, color: '#88cc88', marginBottom: 6 }}>
                    Signed by <em style={{ fontFamily: 'Georgia,serif', fontSize: 18, color: '#a0c4ff' }}>{agreement.signature_name}</em>
                  </div>
                  <div style={{ fontSize: 13, color: C.muted }}>on {fmtDate(agreement.signed_at)}</div>
                  <div style={{ fontSize: 12, color: C.muted, marginTop: 14 }}>AKCODA has been notified. A confirmation will be sent to {agreement.customer_email}.</div>
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: C.text, marginBottom: 6 }}>✍️ Sign this Agreement</div>
                  <div style={{ fontSize: 13, color: C.muted, marginBottom: 18, lineHeight: 1.6 }}>Type your full legal name below. Your typed name serves as your electronic signature.</div>
                  <input
                    style={{ width: '100%', padding: '16px 20px', background: C.surface2, border: `2px solid ${sigName ? C.brand : C.border}`, borderRadius: 12, fontSize: 24, fontFamily: 'Georgia,serif', color: '#a0c4ff', outline: 'none', transition: 'all .2s', boxShadow: sigName ? `0 0 0 4px ${C.brandGlow}` : 'none' }}
                    placeholder="Your full name…"
                    value={sigName}
                    onChange={e => setSigName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && sigName.trim().length >= 2 && setConfirm(true)}
                  />
                  <div style={{ fontSize: 12, color: C.muted, marginTop: 10, marginBottom: 22 }}>By clicking below you confirm you have read and agree to all terms above.</div>
                  <Btn variant="primary" size="lg" disabled={sigName.trim().length < 2} onClick={() => setConfirm(true)}
                    style={{ width: '100%', justifyContent: 'center', boxShadow: sigName.trim().length >= 2 ? `0 6px 24px ${C.brandGlow}` : 'none', opacity: sigName.trim().length < 2 ? 0.4 : 1 }}>
                    ✓ Confirm & Sign Agreement
                  </Btn>
                </div>
              )}
            </div>
          </div>

          <div style={{ borderTop: `1px solid ${C.border}`, padding: '14px 38px', display: 'flex', justifyContent: 'space-between', background: C.bg }}>
            <span style={{ fontSize: 11, color: C.border2 }}>agreement.akcoda.in</span>
            <span style={{ fontSize: 11, color: C.border2 }}>Ref: {id.slice(-8).toUpperCase()}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
